<!--
  בהמתנה - the hourglass in the header icon row (asked 5/11/2026: "a display notification for when
  there is a new הזמנה that is not signed yet, and when we are waiting for a customer to fill in
  booking requests"). The badge = what waits; the box lists it:
    - הזמנות waiting for the customer's signature (needs tax_documents.view) → the order's page
    - online booking requests waiting for the business's approval → /booking-requests
    - customers who got the booking link (תיאום פגישות) and have not booked yet → their card, or the
      whatsapp chat to remind them
    - contacts who were sent the contact form (בקשת מילוי פרטי לקוח, the contacts tab) and have not
      filled it → the card's contacts tab, the whatsapp chat, or off the list by hand (5/11/2026)
  The api decides what is in it (apiv2 modules/awaiting): a section the user may not see, or the
  account does not have, comes back null and is not shown.

  Mounted twice like the whatsapp box beside it - the desktop row and the mobile one, CSS hides
  one. The data is shared (modules/AwaitingState.js), so both show the same count and only one
  request goes out. On the phone the icon shows only while something waits (the row is narrow);
  on the computer it stays, with no badge when nothing waits.
-->
<template>

    <li v-if="visible"
        class="headerAwaitingButton"
        @click="toggleBox"
        :style="mobile ? 'list-style-type:none;padding:12px;font-size:20px;position:relative;top:-2px;' : ''"
        title="ממתינים לחתימה, לתיאום פגישה ולמילוי פרטים"
        v-bind:class="{ highlightHeaderButton: showBox }">

        <i class="fa-regular fa-hourglass-half"
           :style="mobile ? 'position:relative;top:2px;' : 'position:relative;top:3px;'"></i>

        <span v-if="count > 0"
              class="notification-badge headerAwaitingBadge">{{ count > 99 ? '99+' : count }}</span>


        <div v-show="showBox"
             @click.stop
             class="card headerAwaitingCard"
             :style="mobile ? 'position:fixed;top:64px;left:8px;right:8px;width:auto;' : 'position:absolute;left:0;top:80px;width:420px;'">

            <div class="headerAwaitingTitle">
                בהמתנה
                <i v-if="loading"
                   class="fa-solid fa-spinner fa-spin headerAwaitingSpinner"
                   style="font-size:12px;"></i>
            </div>


            <div v-if="!data"
                 class="headerAwaitingEmpty">
                <i class="fa-solid fa-spinner fa-spin"
                   style="font-size:16px;"></i>
            </div>


            <template v-else>

                <div v-if="count == 0"
                     class="headerAwaitingAllClear">
                    <i class="fa-regular fa-circle-check"
                       style="font-size:22px;color:#2e9e6b;"></i>
                    <div>אין כרגע הזמנות, תיאומי פגישות או בקשות לפרטים שממתינים</div>
                </div>


                <!-- the orders waiting for a signature -->
                <div v-if="data.orders && data.orders.count > 0"
                     class="headerAwaitingSection headerAwaitingOrders">

                    <div class="headerAwaitingSectionTitle">
                        <i class="fa-solid fa-file-signature"
                           style="font-size:14px;"></i>
                        הזמנות שממתינות לחתימת הלקוח
                        <span class="headerAwaitingSectionCount">{{ data.orders.count }}</span>
                    </div>

                    <div v-for="row in data.orders.rows"
                         :key="'order_' + row.eventId"
                         class="headerAwaitingRow headerAwaitingOrderRow"
                         @click="openOrder(row)">

                        <div class="headerAwaitingRowMain">
                            <span class="headerAwaitingName">{{ row.contactNames || row.eventName || 'לקוח' }}</span>
                            <span v-if="row.orders[0].daysWaiting < 2"
                                  class="headerAwaitingNew">חדשה</span>
                        </div>

                        <div class="headerAwaitingRowSub">
                            {{ orderLine(row) }}
                        </div>

                        <div class="headerAwaitingAge"
                             :class="{ headerAwaitingLate: row.orders[0].daysWaiting >= 7 }">
                            {{ issuedText(row.orders[0].daysWaiting) }}
                        </div>
                    </div>

                    <div v-if="data.orders.count > data.orders.rows.length"
                         class="headerAwaitingMore">
                        ועוד {{ data.orders.count - data.orders.rows.length }}
                    </div>
                </div>


                <!-- online booking: requests to approve -->
                <div v-if="data.booking && data.booking.pendingCount > 0"
                     class="headerAwaitingSection headerAwaitingPending">

                    <div class="headerAwaitingSectionTitle">
                        <i class="fa-regular fa-calendar-check"
                           style="font-size:14px;"></i>
                        בקשות לתיאום פגישה שממתינות לאישורך
                        <span class="headerAwaitingSectionCount">{{ data.booking.pendingCount }}</span>
                    </div>

                    <div v-for="request in data.booking.pending"
                         :key="'request_' + request.id"
                         class="headerAwaitingRow headerAwaitingPendingRow"
                         @click="go('/booking-requests')">

                        <div class="headerAwaitingRowMain">
                            <span class="headerAwaitingName">{{ request.name || request.phone || 'פונה' }}</span>
                        </div>

                        <div class="headerAwaitingRowSub">
                            {{ requestLine(request) }}
                        </div>

                        <div class="headerAwaitingAge">
                            {{ receivedText(request.createdAt) }}
                        </div>
                    </div>

                    <div class="headerAwaitingMore">
                        <router-link to="/booking-requests"
                                     @click.native="closeBox">לאישור הבקשות</router-link>
                    </div>
                </div>


                <!-- online booking: the link went out, no meeting yet -->
                <div v-if="data.booking && data.booking.invitesCount > 0"
                     class="headerAwaitingSection headerAwaitingInvites">

                    <div class="headerAwaitingSectionTitle">
                        <i class="fa-solid fa-link"
                           style="font-size:14px;"></i>
                        קיבלו קישור לתיאום פגישה וטרם קבעו
                        <span class="headerAwaitingSectionCount">{{ data.booking.invitesCount }}</span>
                    </div>

                    <div v-for="invite in data.booking.invites"
                         :key="'invite_' + invite.customerId"
                         class="headerAwaitingRow headerAwaitingInviteRow"
                         @click="openCustomer(invite)">

                        <div class="headerAwaitingRowMain">
                            <span class="headerAwaitingName">{{ invite.name || 'לקוח' }}</span>

                            <!-- remind them in the chat -->
                            <span v-if="invite.contactId && !whatsappOff"
                                  class="headerAwaitingChat"
                                  title="תזכורת בוואטסאפ"
                                  @click.stop="openChat(invite)">
                                <i class="fa-brands fa-whatsapp"
                                   style="font-size:17px;"></i>
                            </span>
                        </div>

                        <div class="headerAwaitingAge"
                             :class="{ headerAwaitingLate: invite.daysAgo >= 3 }">
                            {{ sentText(invite.daysAgo) }}
                        </div>
                    </div>

                    <div class="headerAwaitingNote">
                        מוצגים קישורים שנשלחו ב-{{ data.booking.inviteDays }} הימים האחרונים
                    </div>
                </div>


                <!-- the contact form (בקשת מילוי פרטי לקוח) went out, not filled yet -->
                <div v-if="data.formRequests && data.formRequests.count > 0"
                     class="headerAwaitingSection headerAwaitingFormRequests">

                    <div class="headerAwaitingSectionTitle">
                        <i class="fa-regular fa-id-card"
                           style="font-size:14px;"></i>
                        קיבלו בקשה למילוי פרטים וטרם מילאו
                        <span class="headerAwaitingSectionCount">{{ data.formRequests.count }}</span>
                    </div>

                    <div v-for="request in data.formRequests.rows"
                         :key="'form_' + request.id"
                         class="headerAwaitingRow headerAwaitingFormRow"
                         @click="openFormRequest(request)">

                        <div class="headerAwaitingRowMain">
                            <span class="headerAwaitingName">{{ request.name || request.contactNames || 'איש קשר' }}</span>

                            <span class="headerAwaitingActions">
                                <!-- remind them in the chat -->
                                <span v-if="!whatsappOff"
                                      class="headerAwaitingChat"
                                      title="תזכורת בוואטסאפ"
                                      @click.stop="openChat(request)">
                                    <i class="fa-brands fa-whatsapp"
                                       style="font-size:17px;"></i>
                                </span>

                                <!-- the details arrived another way -->
                                <span class="headerAwaitingDismiss"
                                      title="הסרה מהרשימה"
                                      @click.stop="dismissFormRequest(request)">
                                    <i class="fa-solid fa-xmark"></i>
                                </span>
                            </span>
                        </div>

                        <div v-if="formRequestLine(request)"
                             class="headerAwaitingRowSub">
                            {{ formRequestLine(request) }}
                        </div>

                        <div class="headerAwaitingAge"
                             :class="{ headerAwaitingLate: request.daysAgo >= 3 }">
                            {{ formSentText(request) }}
                        </div>
                    </div>

                    <div v-if="data.formRequests.count > data.formRequests.rows.length"
                         class="headerAwaitingMore">
                        ועוד {{ data.formRequests.count - data.formRequests.rows.length }}
                    </div>

                    <div class="headerAwaitingNote">
                        מוצגות בקשות שנשלחו ב-{{ data.formRequests.days }} הימים האחרונים
                    </div>
                </div>

            </template>

        </div>

    </li>

</template>
<script>

import { featureDisabled } from '@/core/acl/features';
import axiosV2 from '../../core/http/axios_api_v2';
import { awaitingState, attachAwaiting, detachAwaiting, loadAwaiting, loadIfStale } from '../../modules/AwaitingState';

const DAY_LETTERS = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'];

//'YYYY-MM-DD' → a local Date at midnight (null for anything else)
function dayOf(value) {
    let match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
    return match ? new Date(+match[1], +match[2] - 1, +match[3]) : null;
}

function shortDate(day) {
    return day.getDate() + '/' + (day.getMonth() + 1) + '/' + day.getFullYear();
}

function todayStart() {
    let now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

//whole days from `day` to today (negative = ahead)
function daysBefore(day) {
    return Math.round((todayStart().getTime() - day.getTime()) / 86400000);
}

export default {

    name: 'HeaderAwaitingBox',

    props: ['mobile'],

    data() {
        return {
            showBox: false,
        }
    },

    computed: {

        data() {
            return awaitingState.data;
        },

        loading() {
            return awaitingState.loading;
        },

        count() {
            return (this.data && this.data.count) || 0;
        },

        //the root admin answers support, not customers (like the support button)
        isRootAdmin() {
            let user = this.$store.state.user;
            return !!(user && user.type == 1);
        },

        visible() {
            if (this.isRootAdmin || !this.data) return false;
            if (!this.data.orders && !this.data.booking && !this.data.formRequests) return false;
            return this.mobile ? this.count > 0 || this.showBox : true;
        },

        whatsappOff() {
            return featureDisabled(this.$store, 'whatsapp');
        },
    },

    watch: {

        //a navigation (an order signed in place, a request approved...) - read again when the last read is old
        '$route'() {
            this.closeBox();
            loadIfStale();
        },
    },

    mounted() {

        attachAwaiting(this.$store);

        document.addEventListener('click', this.onDocumentClick);

        this.$root.$on('closeHeaderDropdowns', this.closeBox);
    },

    beforeDestroy() {

        detachAwaiting();

        document.removeEventListener('click', this.onDocumentClick);

        this.$root.$off('closeHeaderDropdowns', this.closeBox);
    },

    methods: {

        toggleBox() {

            if (this.showBox) {
                this.closeBox();
                return;
            }

            //the bell / recent actions / whatsapp box open at the same spot
            this.$root.$emit('closeHeaderDropdowns');

            this.showBox = true;
            loadAwaiting(true);
        },

        closeBox() {
            this.showBox = false;
        },

        onDocumentClick(event) {

            if (!this.showBox)
                return;

            if (this.$el && this.$el.contains && this.$el.contains(event.target))
                return;

            this.closeBox();
        },

        go(path) {
            this.closeBox();
            if (this.$route.fullPath != path)
                this.$router.push(path).catch(() => { });
        },

        openOrder(row) {
            this.go('/taxDocument/' + row.orders[0].taxDocumentId);
        },

        openCustomer(invite) {
            this.go('/customer/' + invite.customerId + (invite.eventId ? '?eventId=' + invite.eventId : ''));
        },

        openChat(invite) {
            this.closeBox();
            this.$root.$emit('globalChatBox', 'whatsapp_contact_' + invite.contactId);
        },

        //the card's contacts tab, on the event the form was sent for
        openFormRequest(request) {
            this.go('/customer/' + request.customerId + (request.eventId ? '?eventId=' + request.eventId : '') + '#contacts');
        },

        //"הסרה מהרשימה" - off the list at once, the box read again after the api took it
        dismissFormRequest(request) {
            let section = this.data && this.data.formRequests;
            if (section) {
                section.rows = section.rows.filter(row => row.id != request.id);
                section.count = Math.max(0, section.count - 1);
                this.data.count = Math.max(0, (this.data.count || 0) - 1);
            }
            axiosV2.post('customer-contact/form-request/' + request.id + '/dismiss', {})
                .catch(() => { })
                .then(() => loadAwaiting(true));
        },

        //הזמנה 1343 · חתונה · האירוע ב-12/12/2026 (two orders on the event: הזמנות 1343, 1340 - a "+1"
        //would print backwards in the rtl line)
        orderLine(row) {
            let parts = [];
            if (row.orders.length > 1)
                parts.push('הזמנות ' + row.orders.slice(0, 3).map(order => order.number).join(', ') + (row.orders.length > 3 ? '...' : ''));
            else
                parts.push('הזמנה ' + row.orders[0].number);
            if (row.eventName && row.eventName != row.contactNames)
                parts.push(row.eventName);
            let day = dayOf(row.eventDate);
            parts.push(day ? 'האירוע ב-' + shortDate(day) : 'ללא תאריך אירוע');
            return parts.join(' · ');
        },

        //יום ה׳ 16/7/2026 · 16:30 · ייעוץ · דנה
        requestLine(request) {
            let parts = [];
            let day = dayOf(request.date);
            if (day) {
                let ahead = -daysBefore(day);
                let when = ahead == 0 ? 'היום' : ahead == 1 ? 'מחר' : 'יום ' + DAY_LETTERS[day.getDay()] + ' ' + shortDate(day);
                parts.push(when + (request.time ? ' ב-' + request.time : ''));
            }
            if (request.meetingTypeName)
                parts.push(request.meetingTypeName);
            if (request.crewMemberName)
                parts.push(request.crewMemberName);
            return parts.join(' · ');
        },

        issuedText(days) {
            if (!days) return 'הופקה היום';
            if (days == 1) return 'הופקה אתמול';
            return 'ממתינה ' + days + ' ימים';
        },

        receivedText(createdAt) {
            let day = dayOf(createdAt);
            if (!day) return '';
            let days = daysBefore(day);
            if (days <= 0) return 'התקבלה היום';
            if (days == 1) return 'התקבלה אתמול';
            return 'התקבלה לפני ' + days + ' ימים';
        },

        sentText(days) {
            if (!days) return 'הקישור נשלח היום';
            if (days == 1) return 'הקישור נשלח אתמול';
            return 'הקישור נשלח לפני ' + days + ' ימים';
        },

        //קרן, עמית · חתונה · האירוע ב-12/12/2026 (the card's names when the row's contact is one of several)
        formRequestLine(request) {
            let parts = [];
            if (request.contactNames && request.contactNames != request.name)
                parts.push(request.contactNames);
            if (request.eventName && request.eventName != request.contactNames && request.eventName != request.name)
                parts.push(request.eventName);
            let day = dayOf(request.eventDate);
            if (day)
                parts.push('האירוע ב-' + shortDate(day));
            return parts.join(' · ');
        },

        formSentText(request) {
            let days = request.daysAgo;
            let when = !days ? 'היום' : days == 1 ? 'אתמול' : 'לפני ' + days + ' ימים';
            if (request.sendCount > 1)
                return 'הבקשה נשלחה ' + request.sendCount + ' פעמים, לאחרונה ' + when;
            return 'הבקשה נשלחה ' + when;
        },
    }

}

</script>
<style>
.headerAwaitingButton {
    cursor: pointer;
    user-select: none;
}

/* the count wears the bell's own badge (HeaderNav's .notification-badge) - the owner, 5/11/2026: "make
   these the same style ... the numbers". It used to be orange. */

.headerAwaitingCard {
    box-shadow: 0 5px 7px 0 rgba(0, 0, 0, .1);
    border-radius: 0 0 6px 6px;
    border: solid 1px #eeee;
    background: white;
    max-height: 520px;
    overflow-y: auto;
    font-size: 14px;
    text-align: right;
    direction: rtl;
    cursor: default;
    z-index: 20;
}

.headerAwaitingTitle {
    background: #eeeeee;
    text-align: center;
    font-size: 18px;
    font-weight: 600;
    position: relative;
    padding: 2px 0;
}

.headerAwaitingSpinner {
    position: absolute;
    left: 12px;
    top: 50%;
    margin-top: -6px;
    color: #999;
}

.headerAwaitingEmpty {
    text-align: center;
    padding: 20px;
}

.headerAwaitingAllClear {
    text-align: center;
    color: #666;
    padding: 22px 15px;
    line-height: 1.6;
}

.headerAwaitingSection {
    border-bottom: solid 1px #eeeeee;
}

.headerAwaitingSection:last-child {
    border-bottom: none;
}

.headerAwaitingSectionTitle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 15px 6px 15px;
    font-weight: 600;
    color: #2a3142;
}

.headerAwaitingSectionTitle i {
    color: #ab8ce4;
}

.headerAwaitingSectionCount {
    margin-right: auto;
    background: #f3eefc;
    color: #7c5cc4;
    border-radius: 10px;
    padding: 0 8px;
    font-size: 12px;
    line-height: 20px;
    min-width: 22px;
    text-align: center;
}

.headerAwaitingRow {
    padding: 8px 15px;
    cursor: pointer;
    line-height: 1.45;
}

.headerAwaitingRow:hover {
    background: #f6f7fb;
}

.headerAwaitingRowMain {
    display: flex;
    align-items: center;
    gap: 8px;
}

.headerAwaitingName {
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}

.headerAwaitingNew {
    flex: none;
    background: #e8590c;
    color: white;
    border-radius: 8px;
    font-size: 11px;
    padding: 0 7px;
    line-height: 17px;
}

.headerAwaitingChat {
    flex: none;
    margin-right: auto;
    color: #25d366;
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.headerAwaitingChat:hover {
    background: #e9f9ef;
}

/* a row's buttons at the far end (the chat + "הסרה מהרשימה") */
.headerAwaitingActions {
    flex: none;
    margin-right: auto;
    display: inline-flex;
    align-items: center;
    gap: 2px;
}

.headerAwaitingActions .headerAwaitingChat {
    margin-right: 0;
}

.headerAwaitingDismiss {
    flex: none;
    color: #aaa;
    width: 30px;
    height: 30px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 14px;
}

.headerAwaitingDismiss:hover {
    background: #f1f1f4;
    color: #666;
}

.headerAwaitingRowSub {
    color: #555;
    font-size: 13px;
}

.headerAwaitingAge {
    color: #999;
    font-size: 12px;
}

.headerAwaitingLate {
    color: #c92a2a;
}

.headerAwaitingMore {
    padding: 2px 15px 10px 15px;
    font-size: 12px;
    color: #888;
}

.headerAwaitingMore a {
    color: #ab8ce4;
}

.headerAwaitingNote {
    padding: 2px 15px 10px 15px;
    font-size: 12px;
    color: #aaa;
}
</style>
